import React, { useState } from "react";
import { Button } from "@mui/material";
import { ExpandMoreRounded, MoreHorizRounded } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "context";
import { WinDims } from "hooks";
import { activeMenuPath, buildMenu } from "components/body/menuConfig";
import MenuPanel from "./MenuPanel";

/**
 * Horizontal top-menu navigation for the "modern" UI template.
 * Reads the same access-gated tree as the classic sidebar (buildMenu), so the
 * two navigations can never drift. Groups that don't fit collapse into "More".
 */
const TopMenu = () => {
  const { access, settings } = useAppContext();
  const { t } = useTranslation("nav");
  const location = useLocation();
  const width = WinDims().width;

  const [anchor, setAnchor] = useState(null);
  const [openKey, setOpenKey] = useState(null);

  // "Home" is already provided as an icon in the NavShortcuts cluster, so drop
  // it here to avoid a duplicate Home entry in the top menu.
  const groups = buildMenu(access, settings, t).filter((g) => g.key !== "home");

  // How many top-level items fit before overflowing into "More".
  // The menu shares its row only with the Recent/Favorites/Home cluster.
  const maxVisible = Math.max(3, Math.floor((width - 220) / 120));
  const visible = groups.slice(0, maxVisible);
  const overflow = groups.slice(maxVisible);

  const activePath = activeMenuPath(location.pathname, groups);
  const isActive = (g) => {
    if (g.path) return g.path === activePath;
    return (g.children || []).some((c) => c.path === activePath);
  };

  const openMenu = (e, key) => {
    setAnchor(e.currentTarget);
    setOpenKey(key);
  };
  const closeMenu = () => {
    setAnchor(null);
    setOpenKey(null);
  };

  const btnSx = (active) => ({
    color: "var(--white-color)",
    opacity: active ? 1 : 0.85,
    fontWeight: active ? 600 : 400,
    // Proper horizontal spacing between menus; minimal top/bottom padding.
    fontSize: "14px",
    px: "12px",
    py: "1px",
    minWidth: "unset",
    minHeight: "unset",
    lineHeight: 1.4,
    borderRadius: "6px",
    backgroundColor: active ? "var(--whiteXS-color)" : "transparent",
    "& .MuiButton-endIcon": { ml: "1px", "& > svg": { fontSize: 16 } },
    "& .MuiButton-startIcon": { mr: "2px", "& > svg": { fontSize: 16 } },
    "&:hover": { backgroundColor: "var(--whiteXS-color)", opacity: 1 },
  });

  const renderGroupButton = (g) => {
    const active = isActive(g);
    if (g.path && !(g.children && g.children.length)) {
      // Render as a real <a href> (router Link) so the browser's native
      // "Open in New Tab" works, matching the classic sidebar's NavLink items.
      return (
        <Button
          key={g.key}
          component={Link}
          to={g.path}
          disableElevation
          onClick={closeMenu}
          sx={btnSx(active)}
        >
          {g.title}
        </Button>
      );
    }
    return (
      <Button
        key={g.key}
        disableElevation
        onClick={(e) => openMenu(e, g.key)}
        endIcon={<ExpandMoreRounded />}
        sx={btnSx(active)}
      >
        {g.title}
      </Button>
    );
  };

  const openGroup = groups.find((g) => g.key === openKey);
  const overflowOpen = openKey === "__more__";

  // Build the panel's sections from whichever menu is open.
  const sections = overflowOpen
    ? overflow.map((g) =>
        g.children && g.children.length
          ? { key: g.key, title: g.title, items: g.children }
          : { key: g.key, items: [{ title: g.title, path: g.path }] }
      )
    : openGroup
    ? [{ key: openGroup.key, title: openGroup.title, items: openGroup.children || [] }]
    : [];

  return (
    <div className="flex items-center gap-[2px] flex-wrap">
      {visible.map(renderGroupButton)}

      {overflow.length > 0 && (
        <Button
          disableElevation
          onClick={(e) => openMenu(e, "__more__")}
          endIcon={<ExpandMoreRounded />}
          startIcon={<MoreHorizRounded />}
          sx={btnSx(overflow.some(isActive))}
        >
          {t("more", "More")}
        </Button>
      )}

      {/* Mega-menu dropdown (group menus + "More" overflow) */}
      <MenuPanel
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={closeMenu}
        sections={sections}
        activePath={activePath}
        align={overflowOpen ? "right" : "left"}
      />
    </div>
  );
};

export default TopMenu;
